import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api';

const SetNewAccountPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const { setNewAccountPassword, error, isLoading, clearError, newAccountPasswordSetSuccess } = useAuth();

  const validateToken = useCallback(async (tokenToValidate: string) => {
    if (newAccountPasswordSetSuccess) {
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/web/auth/validate-setup-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenToValidate }),
      });

      if (response.ok) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (err) {
      console.error('Token validation failed:', err);
      setTokenValid(false);
    }
  }, [newAccountPasswordSetSuccess]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
    } else {
      setTokenValid(false);
    }
  }, [validateToken]);

  useEffect(() => {
    if (newAccountPasswordSetSuccess) {
      setTokenValid(true);
    }
  }, [newAccountPasswordSetSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError('');

    if (!token || !password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(t('auth.setNewAccountPassword.errors.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setValidationError(t('auth.setNewAccountPassword.errors.passwordTooShort'));
      return;
    }

    await setNewAccountPassword(token, password);
  };

  if (tokenValid === null) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </div>
                <p className="mt-2">{t('auth.setNewAccountPassword.validatingToken')}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (tokenValid === false) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <div className="text-danger mb-3">
                  <div style={{ fontSize: '4rem' }}>❌</div>
                </div>
                <h4 className="mb-3 text-danger">{t('auth.setNewAccountPassword.linkExpired')}</h4>
                <p className="mb-4">
                  {t('auth.setNewAccountPassword.linkExpiredMessage')}
                </p>
                <div className="alert alert-info">
                  <small>
                    <strong>Tip:</strong> {t('auth.setNewAccountPassword.linkExpiredTip')}
                  </small>
                </div>
                <Button
                  variant="primary"
                  onClick={() => {
                    window.history.replaceState({}, '', window.location.pathname);
                    window.location.href = '/account-aanvragen';
                  }}
                >
                  {t('auth.setNewAccountPassword.requestNewLink')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (newAccountPasswordSetSuccess) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <div className="text-success mb-3">
                  <div style={{ fontSize: '4rem' }}>✅</div>
                </div>
                <h4 className="mb-3 text-success">{t('auth.setNewAccountPassword.passwordSetSuccess')}</h4>
                <p className="mb-4">
                  {t('auth.setNewAccountPassword.passwordSetMessage')}
                </p>
                <div className="alert alert-info">
                  <small>
                    <strong>Tip:</strong> {t('auth.setNewAccountPassword.passwordSetTip')}
                  </small>
                </div>
                <Button
                  variant="primary"
                  href="/"
                  as="a"
                  className="me-2"
                >
                  {t('auth.setNewAccountPassword.continueButton')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={4}>
          <Card>
            <Card.Body>
              <h4 className="text-center mb-4">{t('auth.setNewAccountPassword.title')}</h4>

              <div className="alert alert-success mb-4">
                <small>
                  {t('auth.setNewAccountPassword.welcome')}
                </small>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={clearError}>
                  {error}
                </Alert>
              )}

              {validationError && (
                <Alert variant="danger" dismissible onClose={() => setValidationError('')}>
                  {validationError}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.setNewAccountPassword.newPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.setNewAccountPassword.newPasswordPlaceholder')}
                    minLength={6}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.setNewAccountPassword.confirmPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.setNewAccountPassword.confirmPasswordPlaceholder')}
                    minLength={6}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading || !token || !password || !confirmPassword || password !== confirmPassword}
                  >
                    {isLoading ? t('auth.loading') : t('auth.setNewAccountPassword.setPasswordButton')}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SetNewAccountPasswordPage;
